/** @jsxImportSource @emotion/react */
import { darken, lighten } from '@material-ui/core'
import { CardColor } from '@signal/components/PureCard'
import { getMainColor } from '@signal/components/PureCard/utils'
import estyled, { ecss } from '@signal/theme/emotion'
import {
  getColorGreySelector,
  getColorPrimarySelector
} from '@signal/theme/selectors'

interface PureButtonWrapperProps {
  $color?: CardColor
  $isDarkMode?: boolean
  $disabled?: boolean
  $fullHeight?: boolean
  $circle?: boolean
  $height?: number
  $fontSize?: number
  $lighten?: boolean
  // If using as='a', these are required
  href?: string
  target?: string
  rel?: string
}

export const PureButtonWrapper = estyled.button<PureButtonWrapperProps>`
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  border-radius: 100px;
  margin: 0;
  padding: 6px 14px;
  padding-bottom: 6px;
  padding-top: 6px;
  font-size: ${({ $fontSize }) => `${$fontSize}px` || '16px'};
  font-weight: 600;
  border: none;
  outline: none;
  box-sizing: border-box;

  /* Fix for anchor tag height issues */
  line-height: 1;
  text-decoration: none;
  vertical-align: baseline;
  font-family: 'Source Sans Pro', -apple-system, BlinkMacSystemFont, 'Segoe UI',
    Roboto, Oxygen-Sans, Ubuntu, Cantarell, 'Helvetica Neue', sans-serif !important;

  transition: all 0.1s ease-in-out;
  font-smooth: always;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover {
    transform: scale(1.03);
    filter: brightness(0.9);
  }

  ${({ $circle }) =>
    $circle &&
    ecss`
      padding: 6px;
    `}

  ${({ $fullHeight = false }) =>
    $fullHeight &&
    ecss`
      min-height: 100%;
      max-height: 100%;
    `}

  ${({ $height, $circle }) => {
    if ($height && $circle) {
      return ecss`
        height: ${$height}px !important;
        width: ${$height}px !important;
        min-height: ${$height}px !important;
        min-width: ${$height}px !important;
        max-height: ${$height}px !important;
        max-width: ${$height}px !important;
      `
    }

    if ($height) {
      return ecss`
        height: ${$height}px !important;
      `
    }
  }}

  ${({
    $color = 'SHINE_BLUE',
    theme,
    $isDarkMode = false,
    $disabled = false,
    $lighten = false
  }) => {
    const isBaseColor = $color === 'BASE' || $color.includes('WHITE')

    let mainColor = getMainColor($color, theme)
    let newTextColor = 'white'
    let borderColor = 'none'

    if (isBaseColor) {
      mainColor = lighten(getColorPrimarySelector()(theme), 0.9)
      newTextColor = darken(getColorPrimarySelector()(theme), 0.25)
      borderColor = lighten(getColorPrimarySelector()(theme), 0.6)
    }

    if ($lighten && !isBaseColor) {
      newTextColor = mainColor
      // borderColor = lighten(mainColor, 0.65)
      mainColor = lighten(mainColor, 0.83)
    }

    if ($disabled) {
      mainColor = lighten(getColorGreySelector()(theme), 0.35)
    }

    if ($isDarkMode) {
      newTextColor = lighten(mainColor, 0.5) // set text color before darkening its dependency, mainColor
      mainColor = darken(mainColor, 0.6)
      borderColor = darken(mainColor, 0.3)
    }

    return ecss`
      color: ${newTextColor} !important;
      background: ${mainColor};
      border: 1px solid ${borderColor};
      outline: none;

      svg {
        fill: ${newTextColor} !important;
        color: ${newTextColor} !important;
        path {
          fill: ${newTextColor} !important;
        }
      }

      // subtle white inset shadow
      box-shadow: inset 0 2px 5px rgba(255, 255, 255, 0.2),
        inset 0 -3px 3px rgba(0, 0, 0, 0.05);

      // white inset shadow
      ${
        ($lighten || $color === 'BASE') &&
        ecss`
        box-shadow: inset 0 -2px 4px ${lighten(
          getMainColor($color, theme),
          0.75
        )} !important;
      `
      }

      &:hover {
        transform: ${$disabled ? 'none' : 'scale(1.03)'};
      }
    `
  }};
`

export const PureButtonIcon = estyled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  max-width: 20px;
  max-height: 20px;

  svg {
    width: 25px;
    height: 25px;
  }
`
